import AIChatPanel from '../AIChatPanel';
import Sidebar from '../Sidebar';
import NoWorkspaceSidebar from '../sidebar/NoWorkspaceSidebar';
import History from '../History';
import DocumentsSidebar from '../DocumentsSidebar';
import ChatSidebar from '../ChatSidebar';
import ChatMessageView from '../ChatMessageView';
import VaultSidebar from '../VaultSidebar';
import VaultView from '../VaultView';
import GitView from '../git/GitView';
import FtpSidebar from '../FtpSidebar';
import FtpView from '../FtpView';
import CalendarView from '../calendar/CalendarView';
import GraphView from '../graph/GraphView';
import TerminalExtView from '../terminal/TerminalExtView';
import DesktopOnlyNotice from '../web/DesktopOnlyNotice';
import { isDesktopOnlyPanel, isWebApp } from '../../platform/target';
import DesktopSignInPage from '../auth/DesktopSignInPage';
import MailCatcherView from '../mailCatcher/MailCatcherView';
import Teams from '../Teams';
import TeamsArea from '../TeamsArea';
import EnhancedTabManager from '../ui/EnhancedTabManager';
import ActivityBar from '../ActivityBar';
import AppContent from '../AppContent';
import OnboardingHost from '../../onboarding/OnboardingHost';
import UsageConsentCard from '../UsageConsentCard';
import HelpHost from '../../help/HelpHost';
import ExtensionHostLayer from '../../extensions/ExtensionHostLayer';
import ExtensionModalHost from '../../extensions/ExtensionModalHost';
import SshHostKeyHost from '../ssh/SshHostKeyHost';
import McpTerminalApprovalHost from '../McpTerminalApprovalHost';
import NotificationContainer from '../ui/NotificationContainer';
import VaultPassphraseGateHost from '../VaultPassphraseGateHost';
import VaultCloudConsentDialog from '../VaultCloudConsentDialog';
import DbQueryTabPickerModal from '../DbQueryTabPickerModal';
import DbScriptSaveModal from '../DbScriptSaveModal';
import EditorPane from '../EditorPane';
import ApibooLoader from '../ui/ApibooLoader';
import TextContextMenu from '../ui/TextContextMenu';
import ModalStack from '../ModalStack';
import {
  setActiveTab,
  setSettingsModalOpen,
  setSettingsInitialTab,
  setShareItem,
  setShowEnvironmentsModal,
  setAiPanelOpen,
} from '../../store/uiModalStore';
import { setTeams } from '../../store/teamsStore';
import { openTaskManagerBoardTab } from '../../store/tabsStore';
import { useCollectionsStore } from '../../store/collectionsStore';
import TauriTitleBar from '../TauriTitleBar';
import { isTauri } from '../../utils/tauri';
import { isLocalWorkspace, isGitWorkspace } from '../../utils/workspaceStorage';
import DevToolsFooter from '../devtools/DevToolsFooter';
import DevToolsPanel from '../devtools/DevToolsPanel';
import { setEnvironments, setSelectedEnvironment, setGlobalVariables } from '../../store/variablesStore';
import { CLOSE_ALL_SCOPE_BY_TAB_TYPE } from './appTabTypes';

/** The app shell markup: title bar, activity bar, sidebar, the active panel, the AI panel, modals and the desktop gate. Plain function called during render, so the element tree is unchanged. */
export function renderApp({
  t,
  desktopGate,
  teams,
  collections,
  isAuthenticated,
  user,
  sidebarPosition,
  showNavTooltips,
  sidebarWidth,
  isSidebarResizing,
  handleSidebarResizeStart,
  activeTab,
  commandPaletteTeamDetailId,
  aiPanelOpen,
  showToast,
  toasts,
  removeToast,
  schemas,
  history,
  selectedRequest,
  selectedCollection,
  selectedFolder,
  editingItem,
  editingSchemaItem,
  selectedSchemaEntry,
  sidebarFadeInIds,
  forkingWorkspaceId,
  gitSyncingWorkspaceId,
  publishingWorkspaceId,
  requestBuilderRenameRef,
  schemaEditorRenameRef,
  registerRequestBuilderLiveState,
  registerRequestBuilderSendHandler,
  selectRequest,
  environments,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariablesById,
  teamLibraryRefresh,
  workspaces,
  currentWorkspace,
  gitAccount,
  requestTabs,
  collectionTabs,
  schemaTabs,
  activeRequestTabId,
  activeCollectionTabId,
  activeSchemaTabId,
  activeDbManagerTabId,
  activeTabType,
  activeTaskManagerTabId,
  activeProjectsTabId,
  activeDocumentTabId,
  extensionTabType,
  isExtensionContext,
  stripRequestTabs,
  stripCollectionTabs,
  stripSchemaTabs,
  stripDbManagerTabs,
  stripTaskManagerTabs,
  stripProjectsTabs,
  stripDocumentTabs,
  stripTabStripOrder,
  openCollectionTab,
  openSchemaTab,
  updateCurrentRequestTabStable,
  updateCurrentRequestTabResponse,
  clearTabLoading,
  closeTab,
  duplicateTab,
  closeOtherTabs,
  closeAllTabs,
  handleTabsReorder,
  handleTabChange,
  handleNewRequestTab,
  handleRenameSchemaEntry,
  handleSelectSchemaEntry,
  handleStartSchemaRename,
  handleSaveSchemaRename,
  handleCancelSchemaRename,
  handleCommitSchemaName,
  handleDeleteSchemaEntry,
  handleMoveSchemaEntry,
  handleCreateSchema,
  handleCreateSchemaFolder,
  handleDuplicateSchema,
  updateCurrentSchemaTabStable,
  saveSchemaTab,
  reloadWorkspacesFromServer,
  switchWorkspace,
  handleOpenWorkspaceModal,
  handleWorkspaceCreated,
  handleWorkspaceUpdated,
  handleWorkspaceEdit,
  handleWorkspaceDelete,
  handleGitSync,
  handleGitPull,
  handleGitChanges,
  handleGitPutBack,
  handleGitConnectConfirm,
  handleGitAddRemote,
  handleGitStopTracking,
  handleGitResolveConflicts,
  handleWorkspaceCloned,
  requestPublishLocalWorkspace,
  handlePublishLocalConfirm,
  canPublishToNetwork,
  requestPublishToNetwork,
  handleNetworkImported,
  openForkWorkspaceModal,
  handleForkWorkspaceConfirm,
  currentSchemaTab,
  currentCollectionVariables,
  requestCollectionName,
  requestCollectionAuth,
  requestFolderAuthChain,
  handleSidebarDuplicate,
  updateInfo,
  updateModalOpen,
  updateInstalling,
  updateError,
  onUpdateModalClose,
  handleOpenUpdateModal,
  handleManualUpdateCheck,
  handleUpdateLater,
  handleSkipVersion,
  handleInstallUpdate,
  splashVisible,
  splashFading,
  claimExpiry,
  saveRequest,
  handleSelectCollectionForPendingRequest,
  handleCollectionTabDataChange,
  saveCollectionFromTab,
  handleCreateCollection,
  createRequest,
  deleteRequest,
  deleteCollection,
  handleCreateFolder,
  createFolderReturningId,
  handleDeleteFolder,
  handleSidebarCollectionsReorder,
  createCollection,
  openImportCollection,
  openExportCollection,
  openRunCollection,
  openCodeGen,
  handleCollectionRunnerClose,
  handleCollectionRunnerComplete,
  getExportableCollections,
  openExportCollectionFromPalette,
  handleSelectCollectionForExport,
  selectFolder,
  selectFromHistory,
  addToHistory,
  clearHistory,
  handleClearHistory,
  clearHistoryForRequest,
  handleBulkDelete,
  handleBulkDuplicate,
  handleRevealInExplorer,
  handleOpenGitWorkspace,
  handleExportWorkspaceToFolder,
  handleImportCollections,
  handleImportEnvironment,
  handleDroppedImportFile,
  handleTeamsModal,
  handleTeamCreated,
  handleTeamUpdated,
  handleOpenTeamFromCommandPalette,
  clearCommandPaletteTeamDetail,
  handleTeamJoined,
  handleShareWithTeamSuccess,
  openAuthModal,
  handleSignOut,
  saveVariableScope,
  handleVariableUpdate,
  aiCreateCollection,
  aiCreateFolders,
  aiCreateRequest,
  aiMoveRequests,
  aiExecuteRequest,
  aiRunCollection,
  getCurrentEditorRequest,
  aiGetLastResponse,
  aiGenerateTests,
  aiGeneratePreRequestScript,
  aiUpdateRequest,
  aiToolActions,
  handleStartSidebarRename,
  handleSidebarRenameDraft,
  handleCommitRequestName,
  handleSaveSidebarRename,
  handleCancelSidebarRename,
  refreshCollections,
  workspaceReadOnly,
}) {
  return (
    <div
      className="h-screen min-h-0 flex flex-col overflow-hidden app-shell-bg"
      style={isTauri() ? {
        borderRadius: 12,
        // Soft inset ring replaces the harsh dark compositor border at rounded corners.
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.10)',
      } : undefined}
    >
      {/* Owns the app-wide contextmenu listener: suppresses the native menu and
          offers Cut/Copy/Paste when no component claimed the right-click. */}
      <TextContextMenu />

      {/* Splash overlay — covers the cold-start / Ctrl+R blink */}
      {splashVisible && (
        <div className={`apiboo-splash${splashFading ? ' apiboo-splash--out' : ' apiboo-splash--in'}`}>
          <ApibooLoader size={160} />
        </div>
      )}

      {/* Fixed Title Bar. While the sign-in gate is up it is only a floating
          drag strip with the window buttons (TauriTitleBar `gated`), and the
          gate covers the whole window beneath it (owner, 26.10.2026). */}
      <div className="flex-shrink-0">
        <TauriTitleBar
          workspaces={workspaces}
          currentWorkspace={currentWorkspace}
          onWorkspaceChange={switchWorkspace}
          onEditWorkspace={handleWorkspaceEdit}
          onDeleteWorkspace={handleWorkspaceDelete}
          onForkWorkspace={openForkWorkspaceModal}
          forkingWorkspaceId={forkingWorkspaceId}
          onPublishLocalWorkspace={requestPublishLocalWorkspace}
          publishingWorkspaceId={publishingWorkspaceId}
          onPublishToNetwork={requestPublishToNetwork}
          canPublishToNetwork={canPublishToNetwork}
          onOpenWorkspaceModal={() => handleOpenWorkspaceModal()}
          onShareWorkspace={
            isAuthenticated
              ? (ws) => setShareItem({ type: 'workspace', item: ws })
              : undefined
          }
          showToast={showToast}
          environments={environments}
          selectedEnvironment={selectedEnvironment}
          onSelectedEnvironmentChange={setSelectedEnvironment}
          onOpenEnvironmentsModal={() => setShowEnvironmentsModal(true)}
          onOpenAuthModal={openAuthModal}
          onOpenSettings={(tab) => { if (tab) setSettingsInitialTab(tab); setSettingsModalOpen(true); }}
          updateAvailable={Boolean(updateInfo?.updateAvailable)}
          onOpenUpdate={handleOpenUpdateModal}
          onTeamJoined={handleTeamJoined}
          onOpenGitWorkspace={handleOpenGitWorkspace}
          onExportWorkspace={handleExportWorkspaceToFolder}
          collections={collections}
          requestTabs={requestTabs}
          schemas={schemas}
          schemaTabs={schemaTabs}
          gated={desktopGate !== 'app'}
        />
      </div>

      {desktopGate !== 'app' ? (
        <>
          <DesktopSignInPage loading={desktopGate === 'loading'} expired={isAuthenticated && claimExpiry === 'expired'} />
          {/* ModalStack (which renders the notices) is behind the gate; a
              notice such as "signed out: too many devices" lands here. */}
          <NotificationContainer />
        </>
      ) : (
      <>
      {/* Main Layout - Takes remaining height */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      <div className="flex-1 flex overflow-hidden app-main-layout min-h-0">
        <ActivityBar
          activeTab={activeTab}
          onChange={setActiveTab}
          isAuthenticated={isAuthenticated}
          teamsCount={teams.length}
          showNavTooltips={showNavTooltips}
          sidebarPosition={sidebarPosition}
          updateAvailable={Boolean(updateInfo?.updateAvailable)}
          onOpenUpdate={handleOpenUpdateModal}
          t={t}
        />

        <div className="relative flex-1 flex overflow-hidden min-w-0">
        <AppContent
          activeTab={activeTab}
          sidebarPosition={sidebarPosition}
          sidebarWidth={sidebarWidth}
          isSidebarResizing={isSidebarResizing}
          onSidebarResizeStart={handleSidebarResizeStart}
          teamsView={
            <TeamsArea teamsView={
            <Teams
              onOpenModal={handleTeamsModal}
              currentUserId={user?.id}
              teams={teams}
              onTeamsChange={setTeams}
              showToast={showToast}
              workspaces={workspaces}
              openDetailTeamIdFromPalette={commandPaletteTeamDetailId}
              onConsumedOpenDetailTeamIdFromPalette={clearCommandPaletteTeamDetail}
              onShareWithTeam={(team) =>
                setShareItem({
                  type: 'workspace',
                  item: null,
                  initialTab: 'team',
                  fixedTeam: team,
                })
              }
              onForkWorkspace={openForkWorkspaceModal}
              teamLibraryRefresh={teamLibraryRefresh}
              onOpenWorkspace={(ws) => {
                switchWorkspace(ws);
                setActiveTab('collections');
              }}
              onOpenBoard={(board) => {
                openTaskManagerBoardTab({ boardId: board.id, label: board.name });
                setActiveTab('task-manager.panel');
              }}
            />
            } />
          }
          sidebarView={
            isWebApp() && isDesktopOnlyPanel(activeTab) ? null // web app: the notice fills the page
            : activeTab === 'collections' && !currentWorkspace && (workspaces || []).length > 0 ? (
                // The project scope has no workspace (ProjectScopeSync closed the
                // previous project's one): offer to create one, never a stale list.
                <NoWorkspaceSidebar onCreateWorkspace={() => handleOpenWorkspaceModal()} />
            ) : activeTab === 'collections' ? (
                <Sidebar
                  collections={collections}
                  workspaceId={currentWorkspace?.id}
                  isLocalWorkspace={isLocalWorkspace(currentWorkspace, isAuthenticated)}
                  onRefreshCollections={refreshCollections}
                  isAuthenticated={isAuthenticated}
                  onCreateCollection={createCollection}
                  onImportCollection={openImportCollection}
                  onImportFileDrop={handleDroppedImportFile}
                  onExportCollection={openExportCollection}
                  onRunCollection={openRunCollection}
                  onGenerateCode={openCodeGen}
                  onCreateRequest={createRequest}
                  onCreateFolder={handleCreateFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onSelectRequest={selectRequest}
                  onSelectCollection={openCollectionTab}
                  onSelectFolder={selectFolder}
                  onDeleteRequest={deleteRequest}
                  onDeleteCollection={deleteCollection}
                  onBulkDelete={handleBulkDelete}
                  onBulkDuplicate={handleBulkDuplicate}
                  onCreateFolderReturningId={createFolderReturningId}
                  onDuplicate={handleSidebarDuplicate}
                  fadeInIds={sidebarFadeInIds}
                  selectedRequest={selectedRequest}
                  selectedCollection={selectedCollection}
                  selectedFolder={selectedFolder}
                  editingItem={editingItem}
                  onStartRename={handleStartSidebarRename}
                  onSaveRename={handleSaveSidebarRename}
                  onRenameDraft={handleSidebarRenameDraft}
                  onCancelRename={handleCancelSidebarRename}
                  onCollectionsReorder={handleSidebarCollectionsReorder}
                  onShareCollection={isAuthenticated ? (col) => setShareItem({ type: 'collection', item: col }) : undefined}
                  onRevealInExplorer={isLocalWorkspace(currentWorkspace, isAuthenticated) ? handleRevealInExplorer : undefined}
                  schemas={schemas}
                  activeSchemaId={
                    selectedRequest || selectedCollection || selectedFolder
                      ? null
                      : currentSchemaTab?.schemaId
                  }
                  onOpenSchema={openSchemaTab}
                  onCreateSchema={handleCreateSchema}
                  onCreateSchemaFolder={handleCreateSchemaFolder}
                  onRenameSchemaEntry={handleRenameSchemaEntry}
                  onDeleteSchemaEntry={handleDeleteSchemaEntry}
                  onDuplicateSchema={handleDuplicateSchema}
                  onMoveSchemaEntry={handleMoveSchemaEntry}
                  editingSchemaItem={editingSchemaItem}
                  onStartSchemaRename={handleStartSchemaRename}
                  onSaveSchemaRename={handleSaveSchemaRename}
                  onCancelSchemaRename={handleCancelSchemaRename}
                  selectedSchemaEntry={selectedSchemaEntry}
                  onSelectSchemaEntry={handleSelectSchemaEntry}
                />
            ) : activeTab === 'documents.panel' ? (
                <DocumentsSidebar enabled />
            ) : activeTab === 'chat.panel' ? (
                <ChatSidebar />
            ) : activeTab === 'vault.panel' ? (
                <VaultSidebar />
            ) : activeTab === 'ftp.panel' ? (
                <FtpSidebar />
            ) : activeTab === 'calendar.panel' ? null // full-bleed page (owner, fix round 3) — no sidebar, AppContent collapses the column
            : activeTab === 'graph.panel' || activeTab === 'projects.panel' ? null // full-bleed pages (Graph & Projects)
            : activeTab === 'terminal.panel' ? null // full-width panel — tabs carry the "+" picker
            : activeTab === 'mail-catcher.panel' ? null // full-bleed mail client (list + reading pane inside the view)
            : activeTab === 'git' ? null // full-bleed Git page
            : activeTab === 'task-manager.panel' ? null : (
                <History
                  history={history}
                  onSelectFromHistory={selectFromHistory}
                  onClearHistory={clearHistory}
                />
            )
          }
          // Terminal stays MOUNTED across every panel switch, Teams included
          // (hidden, not unmounted) — unmounting would dispose every xterm and
          // kill the local shells and SSH sessions behind them. Tabs live until
          // the user closes them or the app exits (SSH additionally idles out
          // Rust-side after 1h without traffic).
          keepAlive={!isWebApp() && (
            <div
              className="flex-1 flex-row h-full overflow-hidden min-w-0"
              style={{ display: activeTab === 'terminal.panel' ? 'flex' : 'none' }}
            >
              <TerminalExtView active={activeTab === 'terminal.panel'} />
            </div>
          )}
        >
            {isWebApp() && isDesktopOnlyPanel(activeTab) ? (
              <DesktopOnlyNotice />
            ) : activeTab === 'chat.panel' ? (
              <ChatMessageView />
            ) : activeTab === 'vault.panel' ? (
              <VaultView />
            ) : activeTab === 'ftp.panel' ? (
              <FtpView />
            ) : activeTab === 'calendar.panel' ? (
              <CalendarView />
            ) : activeTab === 'graph.panel' ? (
              <GraphView />
            ) : activeTab === 'mail-catcher.panel' ? (
              <MailCatcherView />
            ) : activeTab === 'git' ? (
              <GitView onGitSync={handleGitSync} onGitPull={handleGitPull} onGitAddRemote={handleGitAddRemote} onGitStopTracking={handleGitStopTracking} onLoadChanges={handleGitChanges} onPutBack={handleGitPutBack} gitSyncingWorkspaceId={gitSyncingWorkspaceId} />
            ) : activeTab === 'terminal.panel' ? null : (
            /* Scrollable Main Content Area + AI Panel */
            <div className="flex-1 flex flex-row h-full overflow-hidden min-w-0">
            <div className="flex-1 flex flex-col h-full overflow-hidden min-w-0">
              {/* Fixed Tab Manager */}
              <div className="flex-shrink-0">
                <EnhancedTabManager
                  requestTabs={stripRequestTabs}
                  collectionTabs={stripCollectionTabs}
                  schemaTabs={stripSchemaTabs}
                  dbManagerTabs={stripDbManagerTabs}
                  taskManagerTabs={stripTaskManagerTabs}
                  projectsTabs={stripProjectsTabs}
                  documentTabs={stripDocumentTabs}
                  tabStripOrder={stripTabStripOrder}
                  activeTabId={
                    activeTabType === 'request'
                      ? activeRequestTabId
                      : activeTabType === 'schema'
                        ? activeSchemaTabId
                        : activeTabType === 'db-manager-table'
                          ? activeDbManagerTabId
                          : activeTabType === 'task-manager-board'
                            ? activeTaskManagerTabId
                            : activeTabType === 'projects-project'
                              ? activeProjectsTabId
                            : activeTabType === 'document-editor'
                              ? activeDocumentTabId
                              : activeCollectionTabId
                  }
                  activeTabType={activeTabType}
                  onTabChange={handleTabChange}
                  onTabClose={closeTab}
                  onCloseAllTabs={() => closeAllTabs({ scope: CLOSE_ALL_SCOPE_BY_TAB_TYPE[extensionTabType] || 'collections' })}
                  onCloseOtherTabs={closeOtherTabs}
                  onDuplicateTab={duplicateTab}
                  onNewRequestTab={extensionTabType === 'task-manager-board' || extensionTabType === 'projects-project' || extensionTabType === 'document-editor' ? null : handleNewRequestTab}
                  requestKindsMenu={!isExtensionContext}
                  onTabsReorder={handleTabsReorder}
                />
              </div>

              {/* Editor + Dev Tools panel stack */}
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                {/* `app-editor-scroll` is content zoom's hook (styles/appZoom.css).
                    It wraps EditorPane ALONE: the tab strip is the sibling above
                    and DevToolsPanel the sibling below, so neither scales. */}
                <div className="flex-1 min-h-0 overflow-auto app-editor-scroll">
                  <EditorPane
                    collections={collections}
                    schemas={schemas}
                    currentWorkspace={currentWorkspace}
                    workspaceReadOnly={workspaceReadOnly}
                    currentCollectionVariables={currentCollectionVariables}
                    requestCollectionName={requestCollectionName}
                    requestCollectionAuth={requestCollectionAuth}
                    requestFolderAuthChain={requestFolderAuthChain}
                    selectedRequest={selectedRequest}
                    t={t}
                    handleCollectionTabDataChange={handleCollectionTabDataChange}
                    saveCollectionFromTab={saveCollectionFromTab}
                    handleVariableUpdate={handleVariableUpdate}
                    clearTabLoading={clearTabLoading}
                    updateCurrentRequestTabStable={updateCurrentRequestTabStable}
                    updateCurrentRequestTabResponse={updateCurrentRequestTabResponse}
                    saveRequest={saveRequest}
                    addToHistory={addToHistory}
                    history={history}
                    selectFromHistory={selectFromHistory}
                    clearHistoryForRequest={clearHistoryForRequest}
                    registerRequestBuilderLiveState={registerRequestBuilderLiveState}
                    registerRequestBuilderSendHandler={registerRequestBuilderSendHandler}
                    handleCommitRequestName={handleCommitRequestName}
                    requestBuilderRenameRef={requestBuilderRenameRef}
                    updateCurrentSchemaTabStable={updateCurrentSchemaTabStable}
                    saveSchemaTab={saveSchemaTab}
                    openSchemaTab={openSchemaTab}
                    handleCommitSchemaName={handleCommitSchemaName}
                    schemaEditorRenameRef={schemaEditorRenameRef}
                  />
                </div>

                <DevToolsPanel
                  workspaceRootPath={
                    isGitWorkspace(currentWorkspace) ? currentWorkspace.rootPath : undefined
                  }
                />
              </div>
            </div>
            </div>
            )}
            {/* AI panel — a sibling of EVERY panel (not inside the request
                editor branch), so the footer ghost opens it on Terminal, FTP,
                Vault and Chat too. */}
            {aiPanelOpen && (
              <AIChatPanel
                isOpen={aiPanelOpen}
                onClose={() => setAiPanelOpen(false)}
                collections={collections}
                selectedRequest={selectedRequest}
                selectedEnvironment={selectedEnvironment}
                workspaceVariables={workspaceVariables}
                globalVariables={globalVariables}
                environments={environments}
                currentWorkspaceId={currentWorkspace?.id}
                onSetVariable={(key, value, scope) => {
                  if (scope === 'environment' && selectedEnvironment) {
                    setEnvironments((prev) =>
                      prev.map((env) =>
                        env.id === selectedEnvironment.id
                          ? { ...env, variables: env.variables.map((v) => v.key === key ? { ...v, value } : v).concat(env.variables.some((v) => v.key === key) ? [] : [{ key, value, enabled: true }]) }
                          : env
                      )
                    );
                  } else {
                    setGlobalVariables((prev) =>
                      prev.map((v) => v.key === key ? { ...v, value } : v).concat(prev.some((v) => v.key === key) ? [] : [{ key, value, enabled: true }])
                    );
                  }
                }}
                onCreateCollection={aiCreateCollection}
                onCreateFolders={aiCreateFolders}
                onCreateRequest={aiCreateRequest}
                onMoveRequests={aiMoveRequests}
                onExecuteRequest={aiExecuteRequest}
                getCurrentEditorRequest={getCurrentEditorRequest}
                onRunCollection={aiRunCollection}
                onGetLastResponse={aiGetLastResponse}
                onGenerateTests={aiGenerateTests}
                onGeneratePreRequestScript={aiGeneratePreRequestScript}
                onUpdateRequest={aiUpdateRequest}
                getCollections={() => useCollectionsStore.getState().collections}
                extensionActions={aiToolActions}
              />
            )}
        </AppContent>
        <ExtensionHostLayer
          activeTab={activeTab}
          sidebarWidth={sidebarWidth}
          workspaceId={currentWorkspace?.id}
        />
        <ExtensionModalHost />
        {/* Root-mounted, and here it is the whole feature: an MCP
            client can propose a shell command on the user's server while they
            are on any panel or while the window is in the tray. A listener
            scoped anywhere narrower would mean the event fires, no card paints,
            and the command is refused at 120 s with the user never having been
            asked. Inside the desktop gate on purpose (owner, 16.10.2026):
            while sign-in is required nothing is approved, so a proposal is
            refused at 120 s. */}
        <McpTerminalApprovalHost />
        {/* Same reason again: an SFTP or SSH connect on any panel blocks in
            Rust until the person accepts or cancels an unknown host key. */}
        <SshHostKeyHost />
        {/* Same reason again: `vault-gate-open` is dispatched by the FTP panel,
            the chat share card and the vault store, none of which can rely on
            the Vault panel being the active tab. The host takes a single-owner
            claim, so this instance (mounted at startup) owns the gate and the
            one inside VaultView renders null. */}
        <VaultPassphraseGateHost />
        <VaultCloudConsentDialog />
        <DbQueryTabPickerModal />
        <DbScriptSaveModal />
        </div>
      </div>

      <DevToolsFooter />
      </div>

      {/* All top-level modals/overlays */}
      <ModalStack
        currentWorkspace={currentWorkspace}
        isAuthenticated={isAuthenticated}
        user={user}
        teams={teams}
        workspaces={workspaces}
        collections={collections}
        requestTabs={requestTabs}
        collectionTabs={collectionTabs}
        schemaTabs={schemaTabs}
        environments={environments}
        selectedEnvironment={selectedEnvironment}
        workspaceVariables={workspaceVariables}
        globalVariables={globalVariables}
        collectionVariablesById={collectionVariablesById}
        gitAccount={gitAccount}
        gitSyncingWorkspaceId={gitSyncingWorkspaceId}
        publishingWorkspaceId={publishingWorkspaceId}
        forkingWorkspaceId={forkingWorkspaceId}
        updateInfo={updateInfo}
        updateModalOpen={updateModalOpen}
        updateInstalling={updateInstalling}
        updateError={updateError}
        onUpdateModalClose={onUpdateModalClose}
        handleInstallUpdate={handleInstallUpdate}
        handleUpdateLater={handleUpdateLater}
        handleSkipVersion={handleSkipVersion}
        showToast={showToast}
        toasts={toasts}
        removeToast={removeToast}
        handleCreateCollection={handleCreateCollection}
        createFolderReturningId={createFolderReturningId}
        handleImportCollections={handleImportCollections}
        handleImportEnvironment={handleImportEnvironment}
        handleCollectionRunnerClose={handleCollectionRunnerClose}
        handleCollectionRunnerComplete={handleCollectionRunnerComplete}
        handleClearHistory={handleClearHistory}
        getExportableCollections={getExportableCollections}
        handleSelectCollectionForPendingRequest={handleSelectCollectionForPendingRequest}
        handleSelectCollectionForExport={handleSelectCollectionForExport}
        createCollection={createCollection}
        openImportCollection={openImportCollection}
        openExportCollectionFromPalette={openExportCollectionFromPalette}
        refreshCollections={refreshCollections}
        reloadWorkspacesFromServer={reloadWorkspacesFromServer}
        closeAllTabs={closeAllTabs}
        selectRequest={selectRequest}
        openCollectionTab={openCollectionTab}
        handleTabChange={handleTabChange}
        handleNewRequestTab={handleNewRequestTab}
        handleWorkspaceCreated={handleWorkspaceCreated}
        handleWorkspaceUpdated={handleWorkspaceUpdated}
        handleWorkspaceCloned={handleWorkspaceCloned}
        handlePublishLocalConfirm={handlePublishLocalConfirm}
        handleGitConnectConfirm={handleGitConnectConfirm}
        handleGitResolveConflicts={handleGitResolveConflicts}
        handleForkWorkspaceConfirm={handleForkWorkspaceConfirm}
        switchWorkspace={switchWorkspace}
        handleTeamCreated={handleTeamCreated}
        handleTeamUpdated={handleTeamUpdated}
        handleTeamJoined={handleTeamJoined}
        handleTeamsModal={handleTeamsModal}
        handleOpenTeamFromCommandPalette={handleOpenTeamFromCommandPalette}
        handleShareWithTeamSuccess={handleShareWithTeamSuccess}
        openAuthModal={openAuthModal}
        handleSignOut={handleSignOut}
        handleManualUpdateCheck={handleManualUpdateCheck}
        setEnvironments={setEnvironments}
        setSelectedEnvironment={setSelectedEnvironment}
        saveVariableScope={saveVariableScope}
        handleVariableUpdate={handleVariableUpdate}
        handleNetworkImported={handleNetworkImported}
      />

      {/* First run: the terms (Welcome step) and the role/tools tour. Local-first
          users never reach the sign-in modal, so this is the only place they
          are shown the terms. Recorded on-device only — see FirstRunConsent. */}
      <OnboardingHost />
      {/* "Help us improve Apiboo?" — asked once after sign-in, after the
          onboarding; usage data stays off until the answer is "Share". */}
      <UsageConsentCard />
      {/* Help & docs, opened by the rail "?" or openHelp() from anywhere. */}
      <HelpHost />
      </>
      )}
      
      {/* Debug info - remove in production */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div className="bg-yellow-50 border border-yellow-200 p-2 text-xs">
          <strong>Debug:</strong> 
          Auth: {isAuthenticated ? 'Yes' : 'No'}, 
          InitialLoad: {initialLoadComplete ? 'Yes' : 'No'}, 
          LoadedAfterAuth: {hasLoadedCollectionsAfterAuth ? 'Yes' : 'No'}
          {currentWorkspace && (
            <>
              <br />Workspace "{currentWorkspace.name}" 
              (ID: {currentWorkspace.id}, Type: {currentWorkspace.type}, 
              Visibility: {currentWorkspace.visibility}, 
              Owner: {currentWorkspace.owner_id || currentWorkspace.owner?.id || 'N/A'}, 
              Role: {currentWorkspace.role || 'none'},
              Can Access: {canAccessWorkspace(currentWorkspace, user) ? 'Yes' : 'No'})
            </>
          )}
          {isLoadingCollections && <span className="ml-2 text-orange-600">Loading collections...</span>}
        </div>
      )} */}
    </div>
  );
}
